import { cn } from '@/lib/utils';
import { useState, type AnimationEvent } from 'react';
import { CLU_COLOURS, type CluColours } from './clu-colours';
import { CluBody } from './copilot-icon.component';
import type { CluMove } from './clu-moves';

/** The moves this mark plays itself; pondering is drawn by `CluPonder`. */
export type CluMarkMove = Exclude<CluMove, 'pondering'>;

const MOVE_CLASS: Record<CluMarkMove, string> = {
  salute: 'clu-waving',
  thinking: 'clu-thinking',
  nod: 'clu-nodding',
};

/** The keyframes whose end means the move is done. */
const MOVE_END: Record<CluMarkMove, string> = {
  salute: 'clu-wing',
  thinking: 'clu-think-body',
  nod: 'clu-nod-face',
};

interface CluSaluteProps {
  /** Each increase plays `move` once. */
  waveCount?: number;
  move?: CluMarkMove;
  /** Clu's own by default. */
  colours?: CluColours;
  className?: string;
}

/**
 * Clu's mark, playing a salute, thinking or a nod on cue. At rest it matches
 * `CopilotIcon`; the salute's wing reaches past the box, so don't clip it.
 */
export function CluSalute({
  waveCount = 0,
  move = 'salute',
  colours = CLU_COLOURS,
  className,
}: CluSaluteProps) {
  // Counted from mount: a mark shown again (after pondering) doesn't replay.
  const [movesDone, setMovesDone] = useState(waveCount);
  const playing = waveCount > movesDone;

  const handleAnimationEnd = (event: AnimationEvent<SVGSVGElement>) => {
    if (event.animationName !== MOVE_END[move]) return;
    setMovesDone(waveCount);
  };

  return (
    <svg
      viewBox='0 0 511 511'
      fill='none'
      aria-hidden='true'
      focusable='false'
      data-clu-move={playing ? move : undefined}
      className={cn(
        'shrink-0 overflow-visible select-none',
        playing && MOVE_CLASS[move],
        className
      )}
      onAnimationEnd={handleAnimationEnd}
    >
      <g className='clu-bob'>
        <g transform='translate(255.5 470)'>
          <g className='clu-tilt'>
            <g transform='translate(-255.5 -470)'>
              {/* The wing's root sits just inside the body's right edge. */}
              <g transform='translate(401 300)'>
                <g className='clu-wing'>
                  <path
                    d='M0,-46 C62,-86 150,-106 194,-78 C222,-59 214,-14 176,9 C132,36 62,46 0,46 Z'
                    fill={colours.beak}
                  />
                </g>
              </g>
              <CluBody colours={colours} />
              <g className='clu-face'>
                <ellipse cx='258' cy='219.5' rx='119' ry='96.5' fill='#fff' />
                <g className='clu-look'>
                  <g transform='translate(212 214.5)'>
                    <g className='clu-blink'>
                      <circle r='25' fill={colours.eye} />
                    </g>
                  </g>
                  <g transform='translate(322 199)'>
                    <g className='clu-blink'>
                      <circle r='25' fill={colours.eye} />
                    </g>
                  </g>
                </g>
                <path
                  d='M259,225 C272,217 301,218 311,233 C322,251 311,280 279,298 C276,275 270,248 259,225 Z'
                  fill={colours.beak}
                />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
